import { ActionTree } from "vuex";
import { RootState } from "@/store/types";
import API from "@/axios";
import {
  SubscriptionState,
  SubscriptionPayload,
  UpgradeSubscriptionPayload,
  SetAutomaticPayload,
  Invoice,
} from "../types";
import { AxiosResponse } from "axios";

const actions: ActionTree<SubscriptionState, RootState> = {
  updateSubscription({ dispatch }, payload: UpgradeSubscriptionPayload) {
    return API.post("/subscription/update", {
      ...payload,
    }).then((result) => {
      dispatch("getCurrentSubscriptionPlan");
      return result;
    });
  },
  getCurrentSubscriptionPlan({ commit }) {
    return API.get("/subscription")
      .then((response) => {
        const { data: result } = response;
        if (result) {
          const { subscriptionPlan: plan } = result.data;
          if (plan) {
            commit("setCurrentSubscriptionPlan", plan);
            // Set Price Per Verification
            if (plan.name === "Startup")
              commit("setPricePerVerification", 1.25);
            else if (plan.name === "Growth")
              commit("setPricePerVerification", 1.0);
            else if (plan.name === "Scale")
              commit("setPricePerVerification", 0.85);
          } else {
            commit("setCurrentSubscriptionPlan", {
              name: "Sandbox",
            });
            commit("setPricePerVerification", 0.0);
          }
        }
        return result;
      })
      .catch(() => {
        // Prevents a bug where the price per verification is not reset during account switching if an error is thrown
        // while fetching the current subscription plan.
        commit("setPricePerVerification", 0.0);
      });
  },
  getAllSubscriptionPlans({ commit }) {
    return API.get("/subscription/plans").then((response) => {
      const { data: result } = response;
      commit("setAllSubscriptionPlans", result);
    });
  },
  addCard({ state }, paymentMethodId) {
    return API.post("/settings/cards", {
      paymentMethodId: paymentMethodId,
      setAsDefault: state.cards.length === 0, // The first card to be added is set as the default card
    });
  },
  async deleteCard({ dispatch }, cardId: string) {
    const response = await API.delete(`/settings/cards/${cardId}`, {});
    dispatch("fetchAllCards");
    return;
  },
  fetchAllCards({ commit }) {
    return API.get("/settings/cards").then((response) => {
      if (response.data) {
        const { data: result } = response;
        commit("setAllCards", result.data);
      }
      return response;
    });
  },
  async switchDefaultCard({ dispatch }, cardId: string) {
    const response = await API.post("/settings/cards/set_default", {
      cardId,
    });
    dispatch("fetchAllCards");
    return response;
  },
  getCurrentVendorQuota({ commit }) {
    return API.get("/settings/profile").then((response) => {
      const { data: result } = response.data;
      if (result) {
        const { subscription } = result;
        if (subscription) {
          commit("setVerificationQuota", subscription.verificationQuota);
          commit(
            "setVerificationUsedQuota",
            subscription.verificationQuotaUsed
          );
          commit("setAmlQuota", subscription.amlQuota);
          commit("setAmlQuotaUsed", subscription.amlQuotaUsed);
          commit("setMode", subscription.mode);

          const { mode } = subscription;
          if (mode) {
            /**
             * Mode returns a value 1/0
             * 0 - Account is currently in sandbox mode
             * 1 - Account has an active plan
             * 2 - Account has cancelled their subscription
             */
            commit("setIsAccountSandbox", false);
          } else {
            commit("setIsAccountSandbox", true);
          }
        }
        // Set Autorun checks values
        commit("setAutomaticKyb", result.autoRunCompanyComplianceCheck);
        commit(
          "setAutomaticSanction",
          result.autoRunApplicantAMLSanctionsCheck
        );
        commit("setAutomaticPep", result.autoRunApplicantAMLPEPCheck);
      }
      return result;
    });
  },
  createSubscription(context, payload: SubscriptionPayload) {
    return API.post("/subscription/create", {
      ...payload,
    });
  },
  provisionSubscription() {
    return API.post("/subscription/provision");
  },
  cancelSubscription() {
    return API.post("/subscription/cancel");
  },
  fetchAllInvoices({ commit }) {
    return API.get("/subscription/invoices").then(
      async (res: AxiosResponse) => {
        if (res.data) {
          if (res.data.data) {
            const invoices = res.data.data as Invoice[];
            commit("setAllInvoices", invoices);
          }
        }
      }
    );
  },
  getQuotaInformation({ commit }) {
    return API.get("/settings/profile").then(async (res) => {
      if (res.data) {
        const { subscription } = res.data.data;
        if (subscription) {
          const { quotaExpireAt, quotaStartedAt, mode } = subscription;
          commit("setQuotaExpirationDate", quotaExpireAt);
          commit("setQuotaStartDate", quotaStartedAt);
          commit("setMode", mode);
        }
      }
    });
  },
  setAutomaticSetting({ dispatch }, payload: SetAutomaticPayload) {
    dispatch("updateAutomaticSettings", payload);
    return API.post(`/settings/autorun/${payload.id}`, {
      enabled: payload.enabled,
    });
  },
  updateAutomaticSettings({ commit }, payload: SetAutomaticPayload) {
    if (payload.id === "pep") {
      commit("setAutomaticPep", payload.enabled);
    } else if (payload.id === "sanctions") {
      commit("setAutomaticSanction", payload.enabled);
    } else if (payload.id === "company_compliance") {
      commit("setAutomaticKyb", payload.enabled);
    }
  },
};

export default actions;
